import { useMemo, useState } from 'react';

import {
  Assets,
  Box,
  Container,
  FAQ,
  Headings,
  Link,
  ParsedHtmlContent,
  SearchInputField,
  theme,
  useCheckbox,
  useMediaMin,
  useTextInputField,
} from '@foradevelopment/fora-ui';
import { useLocationState } from '@fora-monorepo/webapp-tools';

import {
  EmploymentReportDeviationFeedback,
  FOKExpirationFeedback,
  IntroText,
  ReportProcessingFeedback,
  SearchFilter,
  SearchFilterInfo,
} from '../../components';
import { useAppSelector } from '../../redux/hooks';
import { EmploymentsOverviewOld } from '../components/EmploymentsOverviewOld';
import { links } from '../../router/links';
import { useTypedTranslation } from '../../i18n/i18n';
import { EmploymentsOverview } from '../components/EmploymentsOverview';

export const EmploymentsStartPage = () => {
  const { t } = useTypedTranslation();

  const [employmentsFilter, setEmploymentsFilter] = useLocationState<{
    showTerminatedEmployments?: boolean;
    showActiveEmployments?: boolean;
    showFutureEmployments?: boolean;
  }>('employmentsFilter', {
    showTerminatedEmployments: false,
    showActiveEmployments: true,
    showFutureEmployments: true,
  });

  const ffManageEmployeeAndEmployment = useAppSelector(
    state => state.foraGlobal.settings.ffManageEmployeeAndEmployment
  );
  const employments = useAppSelector(state =>
    ffManageEmployeeAndEmployment ? state.employment.employmentsNew : state.employment.employments
  );
  const employmentReportStatus = useAppSelector(state => state.employment.employmentReportStatus);
  const faGroups = useAppSelector(state => state.employer.employerInfo.faGroups);
  const expiringFoks = useMemo(() => faGroups.filter(({ isExpiring }) => isExpiring), [faGroups]);
  const searchQueryInput = useTextInputField('');
  const showTerminatedEmployments = useCheckbox(employmentsFilter?.showTerminatedEmployments ?? false);
  const showActiveEmployments = useCheckbox(employmentsFilter?.showActiveEmployments ?? true);
  const showFutureEmployments = useCheckbox(employmentsFilter?.showFutureEmployments ?? true);
  const faqsWithoutEmployments = useAppSelector(state => state.content.content.faqListEmploymentsNoEmployments);
  const faqsWithEmployments = useAppSelector(state => state.content.content.faqListEmployments);
  const [searchEmploymentsListLength, setSearchEmploymentsListLength] = useState<number>(0);
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  const handleEmploymentsListChange = (employmentsListLength: number) => {
    setSearchEmploymentsListLength(employmentsListLength);
  };

  const updateEmploymentsFilter = (filterOption: keyof typeof employmentsFilter, checked: boolean) => {
    setEmploymentsFilter({
      ...employmentsFilter,
      [filterOption]: checked,
    });
  };

  return (
    <>
      <IntroText links={!isDesktop || !employments.length ? [<AddEmployeeLink fullWidth={!isDesktop} />] : []}>
        {employments.length ? <TextWithEmployments /> : <TextWithoutEmployments />}
        <Container>
          <Link fontSize="medium" to={links.employmentsHelp}>
            {t('employments.helpPageLink')}
          </Link>
        </Container>
      </IntroText>
      {employmentReportStatus?.isEmploymentReportProcessing && <ReportProcessingFeedback />}
      {!employmentReportStatus?.isEmploymentReportProcessing &&
        employmentReportStatus?.latestProcessedReportHasDeviationsAndNoPayrollData && (
          <EmploymentReportDeviationFeedback />
        )}
      {!employmentReportStatus?.isEmploymentReportProcessing && <FOKExpirationFeedback expiringFoks={expiringFoks} />}
      {employments.length !== 0 && (
        <>
          <Headings.H3 marginBottom={30}>{t('table.employee.tableTitle')}</Headings.H3>
          <Container
            maxWidth={['100%', '100%', '100%']}
            marginBottom={[20, 20, 0]}
            style={{
              display: isDesktop ? 'flex' : 'block',
              justifyContent: 'space-between',
            }}
          >
            <Box minWidth={[335, 474]}>
              <SearchInputField
                searchQueryInput={searchQueryInput}
                placeholder={isDesktop ? t('common.searchEmployeeLabel') : t('common.searchShortLabel')}
              />
            </Box>
            {isDesktop && (
              <Box>
                <AddEmployeeLink />
              </Box>
            )}
          </Container>
          {ffManageEmployeeAndEmployment ? (
            <>
              <SearchFilter
                checkboxes={[
                  {
                    text: t('common.filter.option.showInactiveEmployments'),
                    props: {
                      ...showTerminatedEmployments,
                      onChange: () => {
                        updateEmploymentsFilter('showTerminatedEmployments', !showTerminatedEmployments.checked);
                        showTerminatedEmployments.onChange();
                      },
                    },
                  },
                  {
                    text: t('common.filter.option.showActiveEmployments'),
                    props: {
                      ...showActiveEmployments,
                      onChange: () => {
                        updateEmploymentsFilter('showActiveEmployments', !showActiveEmployments.checked);
                        showActiveEmployments.onChange();
                      },
                    },
                  },
                  {
                    text: t('common.filter.option.showFutureEmployments'),
                    props: {
                      ...showFutureEmployments,
                      onChange: () => {
                        updateEmploymentsFilter('showFutureEmployments', !showFutureEmployments.checked);
                        showFutureEmployments.onChange();
                      },
                    },
                  },
                ]}
                relatedSearchInputFields={[searchQueryInput]}
                totalAmount={employments.length}
                amountShowing={searchEmploymentsListLength}
              />
              <EmploymentsOverview
                filter={{
                  searchInputValue: searchQueryInput.props.value,
                  showTerminatedEmployments: showTerminatedEmployments.checked,
                  showActiveEmployments: showActiveEmployments.checked,
                  showFutureEmployments: showFutureEmployments.checked,
                }}
                employmentsListChange={handleEmploymentsListChange}
              />
            </>
          ) : null}
          {!ffManageEmployeeAndEmployment ? (
            <>
              {searchEmploymentsListLength !== employments.length ? (
                <SearchFilterInfo
                  amountShowing={searchEmploymentsListLength ? searchEmploymentsListLength : 0}
                  totalAmount={employments.length}
                  clearFilter={() => searchQueryInput.setValue('')}
                  marginTop={20}
                />
              ) : null}
              <EmploymentsOverviewOld
                searchInputValue={searchQueryInput.props.value}
                employmentsListChange={handleEmploymentsListChange}
              />
            </>
          ) : null}
        </>
      )}
      {employments.length !== 0 && !!faqsWithEmployments.length && (
        <FAQ testId="employments-faq" layout={isDesktop ? 'drawer' : 'modal'} questionAnswers={faqsWithEmployments} />
      )}
      {employments.length === 0 && !!faqsWithoutEmployments.length && (
        <FAQ
          testId="employments-noEmployments-faq"
          layout={isDesktop ? 'drawer' : 'modal'}
          questionAnswers={faqsWithoutEmployments}
        />
      )}
    </>
  );
};

const TextWithEmployments = () => {
  const ingress = useAppSelector(state => state.content.content.ingressEmployments);

  return (
    <Container>
      <ParsedHtmlContent htmlContent={ingress?.body ?? ''} styling={{ fontSize: 'large' }} />
    </Container>
  );
};

const TextWithoutEmployments = () => {
  const ingress = useAppSelector(state => state.content.content.ingressEmploymentsNoEmployments);

  return (
    <Container>
      <ParsedHtmlContent htmlContent={ingress?.body ?? ''} styling={{ fontSize: 'large' }} />
    </Container>
  );
};

const AddEmployeeLink = ({ fullWidth }: { fullWidth?: boolean }) => {
  const ffManageEmployeeAndEmployment = useAppSelector(
    state => state.foraGlobal.settings.ffManageEmployeeAndEmployment
  );
  const { t } = useTypedTranslation();
  return (
    <Container marginBottom={20} width={fullWidth ? ['100%', 'auto', '100%'] : 'auto'}>
      <Link
        to={ffManageEmployeeAndEmployment ? links.addEmployee : links.addEmployment}
        styleAs="button"
        hasIcon={false}
        fullWidth
        testId="add-employee-link"
      >
        <Assets.PlusIcon color="white" marginRight={8} />
        {t('common.links.addEmployeeLink')}
      </Link>
    </Container>
  );
};
